import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
    Loader2,
    ArrowLeft,
    ArrowRight,
    Send,
    Mic,
    StopCircle,
    Volume2,
    Turtle,
    MessageCircle,
    Sparkles,
    CheckCircle2,
    XCircle,
    Trophy,
    Plus,
    Wand2,
} from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { handlePaywallError, formatQuotaLine, isQuotaExhausted } from "@/lib/access";
import { useQuotas } from "@/hooks/use-quotas";
import { ttsLanguageOf, voiceOf } from "@/lib/lang";
import SlashMenu from "@/components/SlashMenu";
import { useDashboard } from "@/contexts/DashboardContext";

/* ------------------------- SCENARIO PICKER ------------------------------- */

function ScenarioPicker({ scenarios, onPick, loading, quotaLine, quotaExhausted }) {
    const [freeTopic, setFreeTopic] = useState("");
    return (
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-14" data-testid="scenario-picker">
            <p className="text-xs tracking-[0.28em] uppercase text-inkSoft mb-3">Conversation</p>
            <h1 className="font-serif text-3xl md:text-4xl tracking-tight" data-testid="scenario-picker-title">
                Choisissez un cadre. L&apos;IA joue le personnage.
            </h1>
            <p className="mt-3 text-sm text-inkSoft max-w-2xl">
                Chaque scénario cale la personnalité de l&apos;IA, le niveau de langue et le style de correction sur votre profil.
            </p>
            {quotaLine && (
                <p
                    className={`mt-3 inline-block text-[11px] tracking-wide rounded-full px-3 py-1 border ${
                        quotaExhausted
                            ? "bg-terracotta/10 text-terracotta border-terracotta/40 font-semibold"
                            : "text-inkSoft bg-paperAlt border-lexiBorder"
                    }`}
                    data-testid="conversation-quota"
                >
                    {quotaLine}
                </p>
            )}

            <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {scenarios.map((s) => {
                    const isFree = s.id === "free_topic";
                    const inner = (
                        <>
                            <div className="flex items-start justify-between gap-3">
                                <div className="min-w-0">
                                    <div className="text-2xl leading-none mb-2">{s.emoji}</div>
                                    <h3 className="font-serif text-lg leading-tight">{s.title_fr}</h3>
                                    <p className="text-xs text-inkSoft mt-1">{s.subtitle_fr}</p>
                                    <p className="text-[10px] tracking-[0.22em] uppercase text-terracotta mt-3">
                                        IA · {s.ai_role}
                                    </p>
                                </div>
                                <ArrowRight className="w-4 h-4 text-inkSoft shrink-0 mt-1" />
                            </div>
                            {isFree && (
                                <div className="mt-4 flex flex-col sm:flex-row gap-2">
                                    <input
                                        value={freeTopic}
                                        onChange={(e) => setFreeTopic(e.target.value)}
                                        onClick={(e) => e.stopPropagation()}
                                        placeholder="Ex : parlons de mon dernier voyage à Rome"
                                        data-testid="free-topic-input"
                                        className="flex-1 rounded-xl border border-lexiBorder bg-paper px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta"
                                    />
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            onPick("free_topic", freeTopic || null);
                                        }}
                                        disabled={loading}
                                        data-testid="free-topic-start"
                                        className="inline-flex items-center gap-2 bg-ink text-white hover:bg-emerald rounded-full px-4 py-2 text-sm disabled:opacity-60 transition-colors"
                                    >
                                        Démarrer <ArrowRight className="w-4 h-4" />
                                    </button>
                                </div>
                            )}
                        </>
                    );
                    const commonCls = `text-left bg-white border border-lexiBorder rounded-2xl p-5 hover:border-terracotta hover:shadow-sm transition-all ${
                        isFree ? "sm:col-span-2 lg:col-span-3" : ""
                    }`;
                    return isFree ? (
                        <div
                            key={s.id}
                            data-testid={`scenario-${s.id}`}
                            className={commonCls}
                        >
                            {inner}
                        </div>
                    ) : (
                        <button
                            key={s.id}
                            disabled={loading}
                            onClick={() => onPick(s.id, null)}
                            data-testid={`scenario-${s.id}`}
                            className={`${commonCls} disabled:opacity-60`}
                        >
                            {inner}
                        </button>
                    );
                })}
            </div>
        </div>
    );
}

/* ------------------------- CHAT VIEW ------------------------------------- */

function AudioButton({ text, language, speed, testId }) {
    const { data: dash } = useDashboard();
    const voice = voiceOf(dash?.profile);
    const [busy, setBusy] = useState(false);
    async function play() {
        setBusy(true);
        try {
            const { data } = await api.post("/poc/tts", {
                text,
                language: ttsLanguageOf(language),
                speed,
                voice,
            });
            const audio = new Audio(`data:${data.mime_type};base64,${data.audio_base64}`);
            await audio.play();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setBusy(false);
        }
    }
    const Icon = speed === "slow" ? Turtle : Volume2;
    return (
        <button
            onClick={play}
            disabled={busy || !text}
            data-testid={testId}
            className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-[11px] font-medium bg-ink/90 text-white hover:bg-emerald disabled:opacity-60 transition-colors"
        >
            {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Icon className="w-3 h-3" />}
        </button>
    );
}

function AssistantBubble({ message, language, index }) {
    const [showHelp, setShowHelp] = useState(false);
    return (
        <div className="flex gap-3" data-testid={`chat-msg-assistant-${index}`}>
            <div className="w-8 h-8 rounded-full bg-emerald text-white flex items-center justify-center shrink-0 text-xs font-serif">
                IA
            </div>
            <div className="flex-1 min-w-0">
                <div className="bg-white border border-lexiBorder rounded-2xl rounded-tl-sm px-4 py-3">
                    <div className="flex items-start justify-between gap-3">
                        <p className="font-serif text-base leading-snug whitespace-pre-wrap">
                            {message.content}
                        </p>
                        <div className="flex gap-1 shrink-0">
                            <AudioButton
                                text={message.content}
                                language={language}
                                speed="natural"
                                testId={`chat-tts-natural-${index}`}
                            />
                            <AudioButton
                                text={message.content}
                                language={language}
                                speed="slow"
                                testId={`chat-tts-slow-${index}`}
                            />
                        </div>
                    </div>
                    {message.help_fr && (
                        <div className="mt-2">
                            <button
                                onClick={() => setShowHelp((s) => !s)}
                                data-testid={`chat-help-toggle-${index}`}
                                className="text-[11px] tracking-[0.2em] uppercase text-terracotta hover:underline"
                            >
                                {showHelp ? "Masquer" : "Voir"} l&apos;aide en français
                            </button>
                            {showHelp && (
                                <p className="mt-1 text-sm text-inkSoft italic" data-testid={`chat-help-text-${index}`}>
                                    {message.help_fr}
                                </p>
                            )}
                        </div>
                    )}
                </div>

                {(message.reformulation || message.error_focus) && (
                    <div
                        className="mt-2 bg-paperAlt border border-lexiBorder rounded-2xl px-4 py-3 text-sm"
                        data-testid={`chat-correction-${index}`}
                    >
                        <p className="text-[10px] tracking-[0.24em] uppercase text-terracotta font-semibold mb-1">
                            Correction douce
                        </p>
                        {message.reformulation && (
                            <p className="text-sm">
                                <span className="text-inkSoft text-[11px] uppercase tracking-[0.2em] mr-1">
                                    Reformulation
                                </span>
                                « <span className="font-serif italic">{message.reformulation}</span> »
                            </p>
                        )}
                        {message.error_focus && (
                            <p className="mt-1 text-sm">
                                <span className="text-inkSoft text-[11px] uppercase tracking-[0.2em] mr-1">
                                    Focus
                                </span>
                                <span className="text-ink">« {message.error_focus.erreur} »</span>
                                {" → "}
                                <span className="font-serif italic text-emerald">
                                    {message.error_focus.suggestion}
                                </span>
                                <br />
                                <span className="text-xs text-inkSoft">{message.error_focus.explication_fr}</span>
                            </p>
                        )}
                        {message.retry_prompt && (
                            <p className="mt-1 text-xs italic text-inkSoft">{message.retry_prompt}</p>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}

function UserBubble({ message, index }) {
    return (
        <div className="flex gap-3 justify-end" data-testid={`chat-msg-user-${index}`}>
            <div className="max-w-[85%] bg-ink text-white rounded-2xl rounded-tr-sm px-4 py-3 text-sm">
                <p className="whitespace-pre-wrap">{message.content}</p>
            </div>
            <div className="w-8 h-8 rounded-full bg-terracotta text-white flex items-center justify-center shrink-0 text-xs font-serif">
                Vous
            </div>
        </div>
    );
}

function SummaryPanel({ summary, sessionId, onAdded }) {
    const [checks, setChecks] = useState(() => (summary?.suggested_words || []).map(() => true));
    const [busy, setBusy] = useState(false);
    const { refresh: refreshDashboard } = useDashboard();
    const words = summary?.suggested_words || [];

    async function addSelected() {
        const selected = words.filter((_, i) => checks[i]);
        if (selected.length === 0) return;
        setBusy(true);
        try {
            const { data } = await api.post(`/conversation/session/${sessionId}/add_to_reviews`, {
                words: selected,
            });
            toast.success(`${data.added} mots ajoutés à vos révisions.`);
            refreshDashboard?.();
            onAdded?.(data.added);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setBusy(false);
        }
    }

    return (
        <div className="mt-6 space-y-4" data-testid="conversation-summary">
            <div className="bg-emerald text-white rounded-2xl p-6 md:p-8">
                <p className="text-[11px] tracking-[0.28em] uppercase text-white/70 mb-3">Bilan de conversation</p>
                <Trophy className="w-6 h-6 text-white mb-3" strokeWidth={1.75} />
                <h2 className="font-serif text-2xl md:text-3xl leading-tight">
                    {summary?.encouragement_fr || "Bien joué."}
                </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-white border border-lexiBorder rounded-2xl p-5" data-testid="summary-strengths">
                    <p className="text-[10px] tracking-[0.22em] uppercase text-inkSoft mb-2">Points forts</p>
                    <ul className="list-disc list-inside text-sm space-y-1">
                        {(summary?.strengths_fr || []).map((s, i) => (
                            <li key={i}>{s}</li>
                        ))}
                    </ul>
                </div>
                <div className="bg-white border border-lexiBorder rounded-2xl p-5" data-testid="summary-errors">
                    <p className="text-[10px] tracking-[0.22em] uppercase text-inkSoft mb-2">Erreurs principales</p>
                    <ul className="space-y-2 text-sm">
                        {(summary?.main_errors_fr || []).map((e, i) => (
                            <li key={i}>
                                <span className="text-terracotta">« {e.erreur} »</span> →{" "}
                                <span className="font-serif italic text-emerald">{e.correction}</span>
                                <br />
                                <span className="text-xs text-inkSoft">{e.explication_fr}</span>
                            </li>
                        ))}
                    </ul>
                </div>
            </div>

            {(summary?.useful_expressions || []).length > 0 && (
                <div className="bg-white border border-lexiBorder rounded-2xl p-5" data-testid="summary-expressions">
                    <p className="text-[10px] tracking-[0.22em] uppercase text-inkSoft mb-2">Expressions utiles</p>
                    <ul className="text-sm space-y-2">
                        {summary.useful_expressions.map((x, i) => (
                            <li key={i} className="flex flex-col">
                                <span className="font-serif italic">« {x.expression} »</span>
                                <span className="text-xs text-inkSoft">
                                    {x.translation_fr}
                                    {x.usage_hint_fr ? ` · ${x.usage_hint_fr}` : ""}
                                </span>
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            {words.length > 0 && (
                <div className="bg-white border border-lexiBorder rounded-2xl p-5" data-testid="summary-suggested-words">
                    <div className="flex items-center justify-between gap-3">
                        <p className="text-[10px] tracking-[0.22em] uppercase text-inkSoft">
                            Mots à ajouter aux révisions
                        </p>
                        <button
                            onClick={addSelected}
                            disabled={busy}
                            data-testid="add-words-to-reviews"
                            className="inline-flex items-center gap-1.5 text-xs bg-terracotta hover:bg-terracottaDark text-white rounded-full px-3 py-1.5 disabled:opacity-60"
                        >
                            {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3" />}
                            Ajouter la sélection
                        </button>
                    </div>
                    <ul className="mt-3 space-y-2">
                        {words.map((w, i) => (
                            <li key={i} className="flex items-start gap-3">
                                <input
                                    type="checkbox"
                                    checked={checks[i]}
                                    onChange={(e) => {
                                        const nc = [...checks];
                                        nc[i] = e.target.checked;
                                        setChecks(nc);
                                    }}
                                    data-testid={`word-check-${i}`}
                                    className="mt-1 accent-terracotta"
                                />
                                <div className="min-w-0 text-sm">
                                    <div>
                                        <span className="font-serif text-base">{w.word}</span>
                                        <span className="text-inkSoft"> — {w.translation_fr}</span>
                                    </div>
                                    <div className="text-[11px] text-inkSoft italic">
                                        {w.natural_sentence}
                                    </div>
                                </div>
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    );
}

/* ------------------------- MAIN COMPONENT -------------------------------- */

export default function Conversation() {
    const [scenarios, setScenarios] = useState([]);
    const [session, setSession] = useState(null);
    const [loading, setLoading] = useState(true);
    const [text, setText] = useState("");
    const [sending, setSending] = useState(false);
    const [ending, setEnding] = useState(false);
    const [showSlash, setShowSlash] = useState(false);
    const [recording, setRecording] = useState(false);
    const [transcribing, setTranscribing] = useState(false);
    const mrRef = useRef(null);
    const streamRef = useRef(null);
    const chunksRef = useRef([]);
    const bottomRef = useRef(null);
    const navigate = useNavigate();
    const [params] = useSearchParams();
    const { data: quotas, refetch: refetchQuotas } = useQuotas();
    const quotaLine = formatQuotaLine(quotas?.features?.conversation);
    const quotaExhausted = isQuotaExhausted(quotas?.features?.conversation);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/conversation/scenarios");
                setScenarios(data.scenarios);
            } catch (err) {
                toast.error(formatApiError(err));
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    useEffect(() => {
        bottomRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [session?.messages?.length]);

    async function startScenario(scenarioId, freeTopic) {
        setLoading(true);
        try {
            const { data } = await api.post("/conversation/start", { scenario_id: scenarioId, free_topic: freeTopic });
            setSession(data.session);
            refetchQuotas();
        } catch (err) {
            if (!handlePaywallError(err)) toast.error(formatApiError(err));
            refetchQuotas();
        } finally {
            setLoading(false);
        }
    }

    async function sendReply() {
        if (!text.trim() || sending || !session) return;
        const message = text.trim();
        setText("");
        setSending(true);
        // optimistic add
        setSession((s) => ({
            ...s,
            messages: [...s.messages, { role: "user", content: message, at: new Date().toISOString() }],
        }));
        try {
            const { data } = await api.post(`/conversation/session/${session.session_id}/reply`, { text: message });
            setSession((s) => {
                const withoutOptimistic = s.messages.slice(0, -1);
                return {
                    ...s,
                    messages: [...withoutOptimistic, data.user_message, data.assistant_message],
                };
            });
        } catch (err) {
            toast.error(formatApiError(err));
            setSession((s) => ({ ...s, messages: s.messages.slice(0, -1) }));
            setText(message);
        } finally {
            setSending(false);
        }
    }

    async function endConversation() {
        if (!session || ending) return;
        setEnding(true);
        try {
            const { data } = await api.post(`/conversation/session/${session.session_id}/end`);
            setSession(data.session);
            toast.success("Bilan disponible.");
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setEnding(false);
        }
    }

    async function startRecording() {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            streamRef.current = stream;
            const rec = new MediaRecorder(stream);
            chunksRef.current = [];
            rec.ondataavailable = (e) => e.data.size > 0 && chunksRef.current.push(e.data);
            rec.onstop = async () => {
                const blob = new Blob(chunksRef.current, { type: rec.mimeType || "audio/webm" });
                stream.getTracks().forEach((t) => t.stop());
                setRecording(false);
                setTranscribing(true);
                try {
                    const form = new FormData();
                    form.append("audio", blob, "voice.webm");
                    const { data } = await api.post(
                        `/conversation/session/${session.session_id}/voice`,
                        form,
                        { headers: { "Content-Type": "multipart/form-data" } },
                    );
                    if (data.transcription) {
                        setText((t) => (t ? `${t} ${data.transcription}` : data.transcription));
                        toast.success("Transcription insérée dans le champ.");
                    } else {
                        toast.error("Aucune transcription. Réessayez.");
                    }
                } catch (err) {
                    toast.error(formatApiError(err));
                } finally {
                    setTranscribing(false);
                }
            };
            rec.start();
            mrRef.current = rec;
            setRecording(true);
        } catch {
            toast.error("Impossible d'accéder au micro.");
        }
    }
    function stopRecording() {
        if (mrRef.current && mrRef.current.state !== "inactive") mrRef.current.stop();
    }

    if (loading) {
        return (
            <div className="min-h-[60vh] flex flex-col items-center justify-center" data-testid="conversation-loading">
                <Loader2 className="w-6 h-6 text-terracotta animate-spin" />
                <p className="mt-3 text-sm text-inkSoft">Chargement…</p>
            </div>
        );
    }

    if (!session) {
        return <ScenarioPicker scenarios={scenarios} onPick={startScenario} loading={loading} quotaLine={quotaLine} quotaExhausted={quotaExhausted} />;
    }

    const ended = !!session.ended_at;
    const scenarioMeta = scenarios.find((x) => x.id === session.scenario_id);

    return (
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-6 md:py-10" data-testid="conversation-view">
            <header className="mb-6">
                <button
                    onClick={() => navigate("/dashboard")}
                    className="text-inkSoft hover:text-ink text-sm inline-flex items-center gap-1"
                    data-testid="conversation-back"
                >
                    <ArrowLeft className="w-4 h-4" /> Tableau de bord
                </button>
                <div className="mt-3 flex items-start justify-between gap-3">
                    <div className="min-w-0">
                        <p className="text-xs tracking-[0.22em] uppercase text-inkSoft">
                            {scenarioMeta?.emoji} · Scénario
                        </p>
                        <h1 className="font-serif text-2xl md:text-3xl leading-tight" data-testid="conversation-title">
                            {session.scenario_title_fr}
                        </h1>
                        <p className="text-xs text-inkSoft mt-1">
                            IA : {session.ai_role} · {session.language_fr} · {session.level}
                        </p>
                    </div>
                    {!ended && (
                        <button
                            onClick={endConversation}
                            disabled={ending}
                            data-testid="conversation-end"
                            className="text-xs bg-white border border-lexiBorder rounded-full px-3 py-1.5 hover:bg-paperAlt disabled:opacity-60 inline-flex items-center gap-1.5"
                        >
                            {ending ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle2 className="w-3 h-3" />}
                            Terminer la session
                        </button>
                    )}
                </div>
            </header>

            {/* Chat log */}
            <div className="space-y-5" data-testid="chat-log">
                {session.messages.map((m, i) =>
                    m.role === "assistant" ? (
                        <AssistantBubble key={i} message={m} index={i} language={session.language_fr} />
                    ) : (
                        <UserBubble key={i} message={m} index={i} />
                    ),
                )}
                <div ref={bottomRef} />
            </div>

            {ended && session.summary && (
                <SummaryPanel
                    summary={session.summary}
                    sessionId={session.session_id}
                    onAdded={() => setSession((s) => ({ ...s }))}
                />
            )}

            {/* Composer */}
            {!ended && (
                <div className="sticky bottom-4 mt-6 z-20">
                    <div className="bg-white border border-lexiBorder rounded-2xl p-3 shadow-sm relative">
                        {showSlash && (
                            <SlashMenu
                                query={text.replace(/^\//, "")}
                                onClose={() => setShowSlash(false)}
                            />
                        )}
                        <div className="flex items-end gap-2">
                            <textarea
                                value={text}
                                onChange={(e) => {
                                    const v = e.target.value;
                                    setText(v);
                                    setShowSlash(v.startsWith("/"));
                                }}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter" && !e.shiftKey) {
                                        e.preventDefault();
                                        if (!text.startsWith("/")) sendReply();
                                    }
                                }}
                                rows={1}
                                placeholder="Répondez à l'IA… (Maj+Entrée = saut de ligne, / pour les commandes)"
                                data-testid="chat-input"
                                className="flex-1 resize-none bg-paper rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta max-h-32 min-h-[42px]"
                            />
                            {!recording ? (
                                <button
                                    onClick={startRecording}
                                    disabled={sending || transcribing}
                                    data-testid="chat-mic"
                                    className="p-2.5 rounded-full bg-paperAlt hover:bg-paper border border-lexiBorder disabled:opacity-60"
                                    title="Enregistrer"
                                >
                                    {transcribing ? (
                                        <Loader2 className="w-4 h-4 animate-spin" />
                                    ) : (
                                        <Mic className="w-4 h-4" />
                                    )}
                                </button>
                            ) : (
                                <button
                                    onClick={stopRecording}
                                    data-testid="chat-mic-stop"
                                    className="p-2.5 rounded-full bg-terracotta text-white"
                                    title="Arrêter"
                                >
                                    <StopCircle className="w-4 h-4 animate-pulse" />
                                </button>
                            )}
                            <button
                                onClick={sendReply}
                                disabled={!text.trim() || sending || text.startsWith("/")}
                                data-testid="chat-send"
                                className="p-2.5 rounded-full bg-ink text-white hover:bg-emerald disabled:opacity-60 transition-colors"
                            >
                                {sending ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                    <Send className="w-4 h-4" />
                                )}
                            </button>
                        </div>
                        <p className="text-[10px] text-inkSoft mt-2 tracking-wide">
                            <Wand2 className="w-3 h-3 inline mr-1" />
                            Tapez « / » pour ouvrir les commandes rapides.
                        </p>
                    </div>
                </div>
            )}
        </div>
    );
}
